// Always use the global tokenManager, removing the local implementation
const variantSelected = document.getElementById('variant-selected');
const input = document.getElementById('location-input');
const noResultsDiv = document.getElementById('no-results-location');
const popupTrigger = document.getElementById('location-popup-trigger');
let selectedPlace = null;

let autocomplete;
// Variable to store pending location data until apply button is clicked
let pendingLocationData = null;

// Debounce function
function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
}

// Add these variables near the top of the file, after other global declarations
let lastShippingRequest = null;
let shippingRequestCooldown = false;
let shippingCache = {};
const SHIPPING_COOLDOWN_MS = 2000; // 2 seconds cooldown between requests

// Add this variable to store the last inventory data
let lastInventoryData = null;

// Add this state abbreviation mapping after the other global variables
const australianStates = {
  'australian capital territory': 'ACT',
  'new south wales': 'NSW',
  'northern territory': 'NT',
  'queensland': 'QLD',
  'south australia': 'SA',
  'tasmania': 'TAS',
  'victoria': 'VIC',
  'western australia': 'WA'
};

// Helper function to get state abbreviation
function getStateAbbreviation(addressComponents) {
  if (!addressComponents || !Array.isArray(addressComponents)) {
    return '';
  }

  // Find the administrative_area_level_1 component (state)
  const stateComponent = addressComponents.find((component) => component.types.includes('administrative_area_level_1'));

  if (!stateComponent) {
    return '';
  }

  const stateName = stateComponent.long_name.toLowerCase();
  return australianStates[stateName] || stateComponent.short_name || '';
}

// New helper function to extract suburb/locality from address components
function getSuburb(addressComponents) {
  if (!addressComponents || !Array.isArray(addressComponents)) {
    return '';
  }

  // First try to find locality (suburb)
  const localityComponent = addressComponents.find((component) => component.types.includes('locality'));

  if (localityComponent) {
    return localityComponent.long_name;
  }

  // Fall back to sublocality if available
  const sublocalityComponent = addressComponents.find((component) => component.types.includes('sublocality'));

  return sublocalityComponent ? sublocalityComponent.long_name : '';
}

function initAutocomplete() {
  // Check if Google Maps API is available
  if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
    console.error('Google Maps API is not loaded. Make sure the Google Maps JavaScript API is included with Places library.');

    // Provide fallback functionality without autocomplete
    if (input) {
      // Add basic input handling without Google Maps features
      setupBasicLocationInput();
    }
    return;
  }

  if (!input) {
    console.error('Cannot find location-input element');
    return;
  }
  const suggestions = document.getElementById('suggestions');
  const coordinatesResult = document.getElementById('coordinates-result');
  const submitLocationButton = document.getElementById('close-popup');
  submitLocationButton.addEventListener('click', async () => {
    submitLocationButton.setAttribute('aria-disabled', 'true');
    submitLocationButton.classList.add('loading');
    submitLocationButton.querySelector('.loading__spinner').classList.remove('hidden');

    // Handle location validation and popup closing
    const inputValue = input.value.trim();
    const [lat, lng] = await getLongitudeLatitude({fullAddress: inputValue});

    if (inputValue === '' || !lat || !lng) {
      // Empty input case - show error message
      if (noResultsDiv) {
        noResultsDiv.style.display = 'block';
        this.disableSubmitLocationButton();

        return
      }
    } else {
      // Valid place selected - hide error and close popup
      if (noResultsDiv) {
        noResultsDiv.style.display = 'none';
      }
    }
    // Save pending location data to session storage when apply button is clicked
    if (pendingLocationData) {
      localStorage.setItem('customerLocation', JSON.stringify(pendingLocationData));
      // Update the announcement bar after saving location
      checkCustomerLocation();
      // Clear pending data
      pendingLocationData = null;
    }

    if (lat && lng) {
      // Fetch nearest store based on saved location
      const variantSelected = document.getElementById('variant-selected') || null;
      const ids = variantSelected ? ['gid://shopify/ProductVariant/' + variantSelected.value] : [];

      getNearestStore(lat, lng, ids)
        .then(([nearestStore, inventoryLevels]) => {
          if (nearestStore) {
            // Update inventory and shipping options
            handleFetchInventoryProducts({ inventoryLevels: inventoryLevels });
          } else {
            console.warn('No nearest store found, using default store');
            handleFetchInventoryProducts({ inventoryLevels: inventoryLevels });
          }
          closeLocationPopup();
        })
        .catch((err) => {
          console.error('Error fetching nearest store:', err);
        });
    }
  });
  // Initial options for Google Places Autocomplete
  const initialOptionsComplete = {
    types: ['postal_code', 'locality', 'sublocality'],
    componentRestrictions: { country: 'au' },
    fields: ['place_id', 'name', 'geometry', 'address_components']
  };

  // Check localStorage on page load and pre-fill input
  const savedLocation = localStorage.getItem('customerLocation');
  if (savedLocation) {
    const locationData = JSON.parse(savedLocation);
    input.value = locationData.address;
    coordinatesResult.innerHTML = `Coordinates: Latitude ${locationData.lat}, Longitude ${locationData.lng}`;
  }

  // Initialize Google Places Autocomplete
  autocomplete = new google.maps.places.Autocomplete(input, initialOptionsComplete);

  // Handle place selection
  autocomplete.addListener('place_changed', function () {
    const place = autocomplete.getPlace();
    selectedPlace = place;
    handleAddressSelection(place);
  });

  // Debounced function for fetching suggestions
  const fetchSuggestions = debounce(function (inputValue) {
    const service = new google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      {
        input: inputValue,
        ...initialOptionsComplete
      },
      function (predictions, status) {
        suggestions.innerHTML = '';
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          predictions.forEach((prediction) => {
            const div = document.createElement('div');
            div.textContent = prediction.description;
            div.classList.add('suggestion-item');
            div.onclick = () => {
              input.value = prediction.description;
              suggestions.innerHTML = '';
              google.maps.event.trigger(input, 'focus');
              google.maps.event.trigger(input, 'keydown', { keyCode: 13 });
            };
            suggestions.appendChild(div);
          });
        }
      }
    );
  }, 300);

  // Add debounced input listener
  input.addEventListener('input', function () {
    if (input.value.trim()) {
      fetchSuggestions(input.value);
    } else {
      suggestions.innerHTML = '';
    }
    selectedPlace = null;
  });

  // Hide suggestions when clicking outside
  document.addEventListener('click', function (e) {
    if (!input.contains(e.target)) {
      suggestions.innerHTML = '';
    }
  });

  // Handle "Use my current location" button
  document.getElementById('use-current-location').addEventListener('click', function () {
    if (navigator.geolocation) {
      const coordinatesResult = document.getElementById('coordinates-result');
      const input = document.getElementById('location-input');
      const locationWrapper = document.querySelector('.announcement-bar_location-wrapper');

      coordinatesResult.innerHTML = 'Locating...';


      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          // Reverse geocode to get address and then save location data
          const geocoder = new google.maps.Geocoder();
          const latlng = { lat: lat, lng: lng };
          geocoder.geocode({ location: latlng }, (results, status) => {
            if (status === google.maps.GeocoderStatus.OK && results[0]) {
              input.value = results[0].formatted_address;
              // Extract country information
              let country = '';
              let countryCode = '';
              const countryComponent = results[0].address_components.find((component) =>
                component.types.includes('country')
              );
              if (countryComponent) {
                country = countryComponent.long_name;
                countryCode = countryComponent.short_name;
              }

              // Check if the location is in Australia
              if (countryCode !== 'AU') {
                noResultsDiv.style.display = 'block';
                coordinatesResult.innerHTML = `Location detected in ${country}. This service is only available for Australian addresses.`;
                return; // Exit early if not in Australia
              }

              selectedPlace = results[0];
              coordinatesResult.innerHTML = `Coordinates: Latitude ${lat}, Longitude ${lng}`;

              // Extract postal code from address components
              let postalCode = '';
              const postalCodeComponent = results[0].address_components.find((component) =>
                component.types.includes('postal_code')
              );
              if (postalCodeComponent) {
                postalCode = postalCodeComponent.long_name;
              }

              // Get state abbreviation
              const stateAbbr = getStateAbbreviation(results[0].address_components);

              // Get suburb/locality
              const suburb = getSuburb(results[0].address_components);

              // Store location data temporarily until apply button is clicked
              pendingLocationData = {
                address: results[0].formatted_address,
                lat: lat,
                lng: lng,
                postal_code: postalCode,
                state_abbr: stateAbbr,
                suburb: suburb
              };

            } else {
              coordinatesResult.innerHTML = 'Could not find address for this location';
            }
          });
        },
        (error) => {
          let errorMessage;
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'The request to get location timed out.';
              break;
            default:
              errorMessage = 'An unknown error occurred.';
          }
          coordinatesResult.innerHTML = 'Unable to get location: ' + errorMessage;
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      coordinatesResult.innerHTML = 'Geolocation is not supported by this browser.';
    }
  });
}

async function getLongitudeLatitude({ address, postcode, fullAddress }) {
  const geocoder = new google.maps.Geocoder();

  const queryAddress = fullAddress || `${address} ${postcode} Australia`;

  if (!queryAddress.trim()) {
    throw new Error("Missing address, postcode, or fullAddress");
  }

  return new Promise((resolve, reject) => {
    geocoder.geocode({ address: queryAddress }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const { lat, lng } = results[0].geometry.location;
        resolve([lat(), lng()]);
      } else {
        resolve([ null, null ]);
      }
    });
  });
}

function saveLocationToSession(option, name) {
  if (!name) {
    if (option.geometry) {
      // Store location data temporarily until apply button is clicked
      pendingLocationData = {
        address: document.getElementById('location-input').value,
        lat: option.geometry.location.lat(),
        lng: option.geometry.location.lng(),
        postal_code: option.postal_code || ''
      };
    }
  } else {
    const nearestStore = {
      ...option
    };

    localStorage.setItem(name, JSON.stringify(nearestStore));
  }
}

function getNearestStore(lat, lng, ids) {
  return new Promise(async (resolve, reject) => {
    if (!lat || !lng) {
      const error = 'Lat and lng are required!';
      console.error(error);
      reject(error);
      return;
    }

    try {
      const baseUrl = `${window.shopUrl}/apps/service/shopify/nearest-store`;
      const eparcelQuery = isEparcelProduct() ? '&isEparcel=true' : '';
      const query = ids
        ? `variantIds=${ids.join(',')}&lat=${lat}&lng=${lng}${eparcelQuery}`
        : `lat=${lat}&lng=${lng}${eparcelQuery}`;

      const apiUrl = `${baseUrl}?${query}`;

      const response = await fetch(apiUrl, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const data = await response.json();
      if (data?.data?.nearestStore.nearestStore) {
        // Save to session storage
        saveLocationToSession(data.data.nearestStore.nearestStore, 'nearestStore');
        saveLocationToSession(data.data.nearestStore.defaultStore, 'defaultStore');
        resolve([data.data.nearestStore.nearestStore, data.data.inventoryLevels]);
      } else {
        // Use default store for basic functionality instead of rejecting
        if (data?.data?.nearestStore?.defaultStore) {
          saveLocationToSession(data.data.nearestStore.defaultStore, 'nearestStore');
          saveLocationToSession(data.data.nearestStore.defaultStore, 'defaultStore');
          resolve([data.data.nearestStore.defaultStore, data.data.inventoryLevels]);
        } else {
          // Resolve with null instead of rejecting to allow the flow to continue
          console.warn('No store data found, using empty store data');
          resolve(null);
        }
      }
    } catch (err) {
      console.error('Fetch nearest store error:', err);
      // Resolve with null instead of rejecting to allow shipping and pickup to still run
      resolve(null);
    }
  });
}

async function handleFetchInventoryProducts({ inventoryLevels, ids, lat, lng }) {
  let inventoryData;

  if (inventoryLevels) {
    inventoryData = inventoryProducts(inventoryLevels);
  } else if (!ids || !lat || !lng) {
    inventoryData = [];
  } else {
    inventoryData = await fetchInventoryProducts(ids, lat, lng);
  }

  // Store the inventory data for future use
  lastInventoryData = inventoryData;

  // Get shipping data but don't call updateShippingOptions here
  // The debouncedFetchShippingCost call will handle the update
  await handleFetchShippingCost();

  // Only call the debounced version which will use lastInventoryData
  debouncedFetchShippingCost();
  renderStorePickup(lastInventoryData);

  return inventoryData;
}

async function fetchInventoryProducts(ids, lat, lng) {
  try {
    const eparcelQuery = isEparcelProduct() ? '&isEparcel=true' : '';
    const apiUrl = `${window.shopUrl}/apps/service/shopify/inventory-by-ids?variantIds=${ids.join(
      ','
    )}&latitude=${lat}&longitude=${lng}${eparcelQuery}`;

    const response = await fetch(apiUrl, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const data = await response.json();

    let dataProducts = [];
    if (!data?.success) return dataProducts;

    dataProducts = inventoryProducts(data.data);

    return dataProducts;
  } catch (err) {
    console.error('Fetch inventory error:', err);
    return [];
  }
}

function inventoryProducts(inventoryLevels) {
  try {
    let dataProducts = [];

    if (inventoryLevels) {
      const listData = Object.values(inventoryLevels);
      dataProducts = listData
        .map((variant) => {
          const inventoryLevel = variant?.inventoryItem?.inventoryLevel;
          const hasInventoryLevel = inventoryLevel && Object.keys(inventoryLevel).length > 0;
          const variantStock = hasInventoryLevel
            ? inventoryLevel?.quantities?.[0]?.quantity ?? 0
            : 0;
          const maxAvailableQuantity =
            typeof variant.maxAvailableQuantity === 'number' ? variant.maxAvailableQuantity : 0;
          const eparcelReadyToShip =
            typeof variant.eparcelReadyToShip === 'boolean'
              ? variant.eparcelReadyToShip
              : maxAvailableQuantity >= 2;

          if (!hasInventoryLevel && variant.maxAvailableQuantity == null && variant.eparcelReadyToShip == null) {
            return null;
          }

          return {
            variantId: variant.id,
            variantStock,
            maxAvailableQuantity,
            eparcelReadyToShip,
            inventoryPolicy: variant.inventoryPolicy,
            tracked: variant.inventoryItem?.tracked,
            location: hasInventoryLevel ? inventoryLevel.location : null
          };
        })
        .filter(Boolean);
    }

    return dataProducts;
  } catch (err) {
    console.error('Fetch inventory error:', err);
    return [];
  }
}

// Store the default button text
const DEFAULT_LOCATION_BUTTON_TEXT = 'Set location'; // Change this to match your theme default text

function toggleLocationPopup() {
  const locationPopup = document.getElementById('location-popup');
  const changeButtons = document.querySelectorAll('.announcement-bar_location-change');
  const elementHtml = document.querySelector('html');

  if (locationPopup.classList.contains('active')) {
    // Popup is currently active, so close it
    locationPopup.classList.remove('active');
      elementHtml.classList.remove('overflow-hidden');

    // Restore the button text based on whether location is set
    const savedLocation = localStorage.getItem('customerLocation');
    if (savedLocation) {
      changeButtons.forEach((button) => {
        button.textContent = 'Change';
      });
    } else {
      // When no location is set, reset to default text
      changeButtons.forEach((button) => {
        button.textContent = DEFAULT_LOCATION_BUTTON_TEXT;
      });
    }
  } else {
    // Popup is not active, so open it
    locationPopup.classList.add('active');
      elementHtml.classList.add('overflow-hidden');

    // Change button text to "Close" while popup is open
    changeButtons.forEach((button) => {
      button.textContent = 'Close';
    });
  }
}

function checkCustomerLocation() {
  const locationPopup = document.getElementById('location-popup');
  const savedLocation = localStorage.getItem('customerLocation');
  const locationWrapper = document.querySelector('.announcement-bar_location-wrapper');
  const deliveryWrapper = document.querySelector('.delivery-location');
  const changeButtons = document.querySelectorAll('.announcement-bar_location-change');

  if (!savedLocation) {
    // No saved location
    // locationPopup.classList.remove('active'); // Ensure popup is hidden initially

    // Reset button classes
    changeButtons.forEach((button) => {
      button.classList.remove('has-location');
      // Preserve original text from theme settings
    });

    // Clear the location wrapper content
    if (locationWrapper) {
      locationWrapper.innerHTML = 'You have not set a location';
    }
    if (deliveryWrapper) {
      deliveryWrapper.innerHTML = 'You have not set a location';
    }
    if (popupTrigger) {
      popupTrigger.textContent = 'Set your location'; // Change this to match your theme default text
    }
  } else {
    // Location exists
    // locationPopup.classList.remove('active'); // Ensure popup is hidden initially

    // Parse the saved location
    const locationData = JSON.parse(savedLocation);

    // Use suburb if available, otherwise fall back to first part of address
    const locality = locationData.suburb || locationData.address.split(',')[0].trim();
    const stateAbbr = locationData.state_abbr || '';

    // Update the location wrapper with the suburb and state abbreviation
    if (locationWrapper) {
      // Create HTML for the location display
      locationWrapper.innerHTML = `
        <span class="announcement-bar_location-selected">
          Your location is ${locality}${stateAbbr ? `, ${stateAbbr}` : ''}
        </span>
      `;
    }

    if (deliveryWrapper) {
      deliveryWrapper.innerHTML = `
        Your location is <span class="location-name">${locality}${stateAbbr ? `, ${stateAbbr}` : ''}</span>
      `;
    }

    // Update popup trigger text
    if (popupTrigger) {
      popupTrigger.textContent = 'Change'; // Change this to match your theme default text
    }

    // Update change button to show "Change"
    changeButtons.forEach((button) => {
      button.classList.add('has-location');
      button.textContent = 'Change';
    });
  }
}

function initLocationPopupHandlers() {
  // Use toggle function for the change button
  const changeButtons = document.querySelectorAll('.announcement-bar_location-change');
  changeButtons.forEach((button) => {
    button.addEventListener('click', toggleLocationPopup);
  });

  // Add event listener for the delivery location trigger button
  if (popupTrigger) {
    popupTrigger.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toggleLocationPopup();
    });
  }

  // Close popup when overlay is clicked
  const popupOverlay = document.querySelectorAll('.close-popup');
  if (popupOverlay) {
    popupOverlay.forEach(item => {
      item.addEventListener('click', function(event) {
        item.addEventListener('click', closeLocationPopup);
      });
    });
  }
}

function closeLocationPopup() {
  const locationPopup = document.getElementById('location-popup');
  const changeButtons = document.querySelectorAll('.announcement-bar_location-change');
    const elementHtml = document.querySelector('html');

  if (locationPopup) {
    locationPopup.classList.remove('active');
      elementHtml.classList.remove('overflow-hidden');
    disableSubmitLocationButton();

    // Restore button text based on location status
    const savedLocation = localStorage.getItem('customerLocation');
    if (savedLocation) {
      changeButtons.forEach((button) => {
        button.textContent = 'Change';
      });
    } else {
      // When no location is set, reset to default text
      changeButtons.forEach((button) => {
        button.textContent = DEFAULT_LOCATION_BUTTON_TEXT;
      });
    }
  }
}

function disableSubmitLocationButton() {
  const locationPopup = document.getElementById('location-popup');
  const submitLocationButton = locationPopup.querySelector('#close-popup');

  submitLocationButton.setAttribute('aria-disabled', 'false');
  submitLocationButton.classList.remove('loading');
  submitLocationButton.querySelector('.loading__spinner').classList.add('hidden');
}

// Function to check if Google Maps API is loaded
function waitForGoogleMaps(callback, timeout = 3000) {
  const startTime = Date.now();

  function checkGoogle() {
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      callback();
    } else if (Date.now() - startTime > timeout) {
      console.error('Google Maps API failed to load within timeout period');
      console.log('Initializing basic location input without Google Maps functionality');
      // Initialize basic functionality without Google Maps
      if (input) {
        setupBasicLocationInput();
      }
    } else {
      setTimeout(checkGoogle, 100);
    }
  }

  checkGoogle();
}

// Add this before initAutocomplete() call
document.addEventListener('DOMContentLoaded', function () {
  // Wait for Google Maps API to be loaded before initializing autocomplete
  waitForGoogleMaps(
    () => {
      initAutocomplete();
    },
    3000 // 3 second timeout
  );

  checkCustomerLocation();
  initLocationPopupHandlers();

  // Check if customer location exists in localStorage
  const savedLocation = localStorage.getItem('customerLocation');
  if (savedLocation && variantSelected) {
    const locationData = JSON.parse(savedLocation);
    const ids = variantSelected ? ['gid://shopify/ProductVariant/' + variantSelected.value] : [];
    // Only call handleFetchInventoryProducts which will handle both inventory and shipping
    handleFetchInventoryProducts({ ids: ids, lat: locationData.lat, lng: locationData.lng });
  }
});

// Replace the handleFetchShippingCost function with this improved version
async function handleFetchShippingCost() {
  const metaProduct = window.meta?.product;

  const userLocation = localStorage.getItem('customerLocation');
  const nearestStore = localStorage.getItem('nearestStore');

  if (!metaProduct || !userLocation) {
    return false;
  }
  // Continue even without nearestStore, but we'll handle that case below

  // Create a cache key based on relevant data
  const cacheKey = `${userLocation}_${nearestStore}_${variantSelected ? variantSelected.value : 'all'}`;

  // Check if we're in cooldown period
  if (shippingRequestCooldown) {
    return lastShippingRequest || false;
  }

  // Check if we have a cached result
  if (shippingCache[cacheKey]) {
    return shippingCache[cacheKey];
  }

  // Set cooldown flag
  shippingRequestCooldown = true;
  setTimeout(() => {
    shippingRequestCooldown = false;
  }, SHIPPING_COOLDOWN_MS);
  const parsedUserLocation = JSON.parse(userLocation);
  let originData = {};

  if (nearestStore) {
    const parsedNearestStore = JSON.parse(nearestStore);
    if (parsedNearestStore && parsedNearestStore.address) {
      originData = {
        country: 'AU',
        postal_code: parsedNearestStore.address.zip || '0000',
        latitude: parsedNearestStore.address.latitude || parsedUserLocation.lat,
        longitude: parsedNearestStore.address.longitude || parsedUserLocation.lng
      };
    }
  }

  // If no valid store was found, default to user's location
  if (!Object.keys(originData).length) {
    originData = {
      country: 'AU',
      postal_code: parsedUserLocation.postal_code || '0000',
      latitude: parsedUserLocation.lat,
      longitude: parsedUserLocation.lng
    };
  }

  const bodyRequest = {
    destination: {
      country: 'AU',
      postal_code: parsedUserLocation.postal_code,
      latitude: parsedUserLocation.lat,
      longitude: parsedUserLocation.lng
    },
    origin: originData,
    items: metaProduct.variants.map((variant) => ({
      quantity: 1,
      price: variant.price,
      vendor: metaProduct.vendor,
      product_id: metaProduct.id,
      variant_id: variant.id,
      grams: variantSelected.getAttribute('data-weight') || 0
    })),
    currency: 'AUD',
    locale: 'en-AU'
  };

  try {
    const response = await fetch(`${window.shopUrl}/apps/service/shipping/rates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(bodyRequest)
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const data = await response.json();

    // Cache the result
    shippingCache[cacheKey] = data;
    lastShippingRequest = data;

    return data;
  } catch (err) {
    console.error('Fetch shipping cost error:', err);
    return false;
  }
}

// Create a debounced version of the shipping cost function
const debouncedFetchShippingCost = debounce(async () => {
  try {
    const data = await handleFetchShippingCost();
    const deliveryBox = document.getElementById('delivery-box');
    const isEparcel = isEparcelProduct();

    if (isEparcel) {
      updateShippingOptions(data, lastInventoryData);
      deliveryBox?.classList.remove('hidden');
      return;
    }

    if (data) {
      updateShippingOptions(data, lastInventoryData);
      deliveryBox?.classList.remove('hidden');
    } else {
      deliveryBox.className = 'hidden';
    }
  } catch (err) {
    console.error('Error in debounced shipping cost fetch:', err);
  }
}, 500);

function handleAddressSelection(place) {
  if (!place || !place.geometry) {
    console.error('Place data is incomplete');
    return;
  }
  // console.log(place);

  const lat = place.geometry.location.lat();
  const lng = place.geometry.location.lng();
  const address = document.getElementById('location-input').value;
  const postalCodeComponent = place.address_components?.find((component) => component.types.includes('postal_code'));
  const postalCode = postalCodeComponent ? postalCodeComponent.long_name : '';
  const stateAbbr = getStateAbbreviation(place.address_components);
  const suburb = getSuburb(place.address_components);

  // Store address temporarily until apply button is clicked
  pendingLocationData = {
    address,
    lat,
    lng,
    postal_code: postalCode,
    state_abbr: stateAbbr,
    suburb: suburb
  };
}

function isEparcelProduct() {
  return variantSelected?.getAttribute('data-is-eparcel') === 'true';
}

function isEparcelInStock(inventoryData) {
  const item = inventoryData?.[0];
  if (!item) return false;
  if (typeof item.eparcelReadyToShip === 'boolean') return item.eparcelReadyToShip;
  if (typeof item.maxAvailableQuantity === 'number') return item.maxAvailableQuantity >= 2;
  return false;
}

function buildShippingItemHtml(rate, inStock) {
  const formattedPrice = rate ? `${rate.currency === 'AUD' ? '$' : ''}${(rate.total_price / 100).toFixed(0)}` : '';
  const priceHtml = rate ? `<span class="shipping-price">${formattedPrice}</span>` : '';
  // eParcel: always show Australia Post copy (screenshot 1 + 2). Non-eParcel keeps existing texts.
  const descriptionHtml = isEparcelProduct()
    ? window.eparcelShippingText
      ? `<div class="shipping-description-text eparcel">${window.eparcelShippingText}</div>`
      : `<div class="shipping-description-text eparcel"><ul><li>This product will be delivered via Australia Post services.</li><li>An estimate of Shipping Costs will be calculated at the checkout.</li></ul></div>`
    : window.shippingText
      ? `<div class="shipping-description-text">${window.shippingText}</div>
            <div class="shipping-description-text eparcel">${window.eparcelShippingText}</div>`
      : '';

  return `
      <div class="shipping-option">
        <div class="shipping-option">
          <div class="title-shipping">
            <span class="shipping-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.5 8.25L22.5 9.375L23.25 12.75H19.5V8.25Z" fill="white"/>
                <rect x="0.75" y="13.5" width="14.25" height="4.5" fill="white"/>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M0.75 4.5H15V8.25H18.75V13.5H23.25V18H18.75H15V12.75H0.75L0.75 4.5Z" fill="#00D0BF"/>
                <circle cx="4.875" cy="18.375" r="1.875" fill="white"/>
                <circle cx="19.125" cy="18.375" r="1.875" fill="white"/>
                <path d="M16.8484 18.752C17.1548 19.8199 18.1393 20.6039 19.3043 20.6039C20.4692 20.6039 21.4537 19.8199 21.7599 18.752H24V11.924C24 9.26111 21.8337 7.09479 19.1708 7.09479H15.7322V3.42188H0L0 18.752H2.37927C2.68561 19.8199 3.67017 20.6039 4.83508 20.6039C6 20.6039 6.98456 19.8199 7.29071 18.752H16.8484ZM22.5939 11.924V12.2203H19.8662V8.5719C21.4213 8.89417 22.5939 10.2746 22.5939 11.924ZM14.3262 4.82794V12.7244H1.40607V4.82794H14.3262ZM4.8349 19.1977C4.20154 19.1977 3.68628 18.6824 3.68628 18.0489C3.68628 17.4155 4.20154 16.9003 4.8349 16.9003C5.46844 16.9003 5.9837 17.4155 5.9837 18.0489C5.9837 18.6824 5.46844 19.1977 4.8349 19.1977ZM7.29071 17.3459C6.98456 16.2781 5.99982 15.4942 4.8349 15.4942C3.67017 15.4942 2.68542 16.2783 2.37927 17.3459H1.40607V14.1304H14.3262V17.3459H7.29071ZM19.3043 19.1977C18.6707 19.1977 18.1555 18.6824 18.1555 18.0489C18.1555 17.4155 18.6709 16.9003 19.3043 16.9003C19.9376 16.9003 20.4529 17.4155 20.4529 18.0489C20.4529 18.6824 19.9376 19.1977 19.3043 19.1977ZM21.7599 17.3459C21.4537 16.2781 20.4692 15.4942 19.3043 15.4942C18.1393 15.4942 17.1548 16.2783 16.8484 17.3459H15.7322V8.50085H18.46V13.6265H22.5939V17.3459H21.7599Z" fill="#064A45"/>
              </svg>
            </span>
            <span class="shipping-title default">${window.shippingLabel}</span>
            <span class="shipping-title eparcel">${window.eparcelShippingLabel}</span>
          </div>
          ${priceHtml}
        </div>
      </div>
       <div class="shipping-description">
        ${
          inStock
            ? `
            <div class="title-shipping-description">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#00D00A"/>
                    <path d="M7 13L10 16L17 9" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                In Stock & Ready to Ship
            </div>
            <p>Available for priority delivery.</p>
        `
            : ''
        }
        ${descriptionHtml}
    </div>`;
}

function getMetafieldByKey(data, key) {
  try {
    const metafields = data?.location?.metafields?.edges;
    if (!metafields || metafields.length === 0) {
      return null;
    }

    const metafield = metafields.find((edge) => edge.node.key === key);

    if (metafield) {
      try {
        return JSON.parse(metafield.node.value);
      } catch (e) {
        return metafield.node.value;
      }
    }

    return null;
  } catch (error) {
    console.error('Error accessing metafields:', error);
    return null;
  }
}

function updateShippingOptions(shippingData, inventoryData) {
  const shippingOptionsContainer = document.getElementById('shipping-options');

  if (!shippingOptionsContainer) {
    console.warn('Shipping options container not found in the DOM');
    return;
  }

  shippingOptionsContainer.innerHTML = '';

  const dataToUse = inventoryData || lastInventoryData;
  const isEparcel = isEparcelProduct();
  const inStock = isEparcel
    ? isEparcelInStock(dataToUse)
    : dataToUse && dataToUse.length > 0 && dataToUse[0].variantStock > 0;

  if (isEparcel) {
    const rateElement = document.createElement('div');
    rateElement.classList.add('shipping-item');
    rateElement.innerHTML = buildShippingItemHtml(null, inStock);
    shippingOptionsContainer.appendChild(rateElement);
    return;
  }

  if (
    !shippingData ||
    !shippingData.data ||
    !shippingData.data.data ||
    !shippingData.data.data.rates ||
    shippingData.data.data.rates.length === 0
  ) {
    shippingOptionsContainer.innerHTML = `<p style="padding: 0 4px;">No shipping options available</p>`;
    return;
  }

  shippingData.data.data.rates.forEach((rate) => {
    const rateElement = document.createElement('div');
    rateElement.classList.add('shipping-item');
    rateElement.innerHTML = buildShippingItemHtml(rate, inStock);
    shippingOptionsContainer.appendChild(rateElement);
  });
}
function renderStorePickup(inventoryData) {
  const pickUpStoreContainer = document.getElementById('pickup-options');

  if (!pickUpStoreContainer) {
    console.warn('Pickup options container not found in the DOM');
    return;
  }

  const defaultLocation = localStorage.getItem('defaultStore');
  const defaultStoreData = defaultLocation ? JSON.parse(defaultLocation) : null;
  const isEparcel = variantSelected ? variantSelected.getAttribute('data-is-eparcel') : 'false';

  const defaultStoreHtml = document.createElement('div');
  defaultStoreHtml.classList.add('default-store');
  if (defaultLocation && defaultStoreData) {
    defaultStoreHtml.innerHTML = `
    <div class='delivery-item'>
        <div class='delivery-content'>
            <div class='delivery-option'>
              <div class="delivery-title">
                <svg width="32" height="24" viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="9.25" y="9.75" width="13.5" height="13.5" fill="white"/>
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M17.5 15H14.5V18.75H11.5V23.25H20.5V18.75H17.5V15Z" fill="#00D0BF"/>
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M25.75 7.5L16 1.125L6.25 7.5V9L6.25 23.25H8.5L8.5 9H23.5V23.25H25.75V9V7.5Z" fill="#00D0BF"/>
                  <path d="M16.0026 0L5.17188 7.0957L5.17187 24H26.8331V7.0957L16.0026 0ZM22.351 22.5945H20.9764V18.1068H18.8488V13.739H13.1875L13.1875 18.1068H11.0598V22.5945H9.65393V9.90033L22.351 9.90033V22.5945ZM17.4431 18.1068H14.593L14.593 15.1445H17.4431V18.1068ZM15.2998 19.5125V22.5945H12.4653V19.5125H15.2998ZM16.7053 19.5125H19.5709V22.5945H16.7053V19.5125ZM25.4276 22.5945H23.7565V8.49463L8.24841 8.49463L8.24841 22.5945H6.57739L6.57739 7.85522L16.0026 1.68036L25.4276 7.85522V22.5945Z" fill="#064A45"/>
                </svg>
                <span class='delivery-title'>Pick Up</span>
              </div>
              <span class='delivery-price'>FREE</span>
            </div>
            <div class='delivery-stores'>
              <div class='delivery-store'>
                <div class='store-header'>
                  <p class='store-name'>${defaultStoreData?.name}</p>
                </div>
                <div class='store-details'>
                  <div>
                    <a href='tel:${defaultStoreData?.address?.phone}' class='store-phone delivery-action'>${defaultStoreData?.address?.phone}</a>
                    <p>
                      ${defaultStoreData?.address?.address1}<br>
                      ${defaultStoreData?.address?.city} ${defaultStoreData?.address?.province} ${defaultStoreData?.address?.zip}, ${defaultStoreData?.address?.country}<br>
                    </p>
                  </div>
                  <p class='store-actions'>
                    <a
                            href='https://www.google.com/maps/dir/?api=1&destination=${defaultStoreData?.address?.address1}'
                            target='_blank'
                            class='delivery-action'
                    >Direction</a
                    >
                  </p>
                </div>
                ${renderOpeningHoursList(defaultStoreData.openingHours)}
              </div>
            </div>
          </div>
    </div>
    `;
  }

  if (!pickUpStoreContainer) {
    console.warn('Pickup options container not found in the DOM');
    return;
  }
  // Always start with empty container
  pickUpStoreContainer.innerHTML = '';

  // Show default store if we don't have specific inventory data
  if (isEparcel === 'true') {
    if (defaultStoreData) {
      pickUpStoreContainer.appendChild(defaultStoreHtml);
    } else {
      pickUpStoreContainer.innerHTML = '';
    }
    return;
  } else {
    // We have inventory data, show it
    if (!inventoryData || inventoryData.length === 0) {
      pickUpStoreContainer.innerHTML = '';
      return;
    } else if (inventoryData && inventoryData.length === 1) {

      inventoryData.forEach((itemStore) => {
        let availablePickup = true;

        if (!itemStore || !itemStore?.location || !itemStore?.location?.localPickupSettingsV2) {
          availablePickup = false;
        } else {
          if (itemStore?.tracked) {
            if (itemStore?.variantStock <= 0 && itemStore?.inventoryPolicy !== 'CONTINUE') {
              availablePickup = false;
            }
          } else {
            if (itemStore?.variantStock < 0) {
              availablePickup = false;
            }
          }
        }

        if (!availablePickup) {
          pickUpStoreContainer.innerHTML = '';
          return;
        }
        const store_link = getMetafieldByKey(itemStore, 'link');
        const openingHours = getMetafieldByKey(itemStore, 'opening_hours');

        const pickUpElement = document.createElement('div');
        pickUpElement.classList.add('delivery-item');

        pickUpElement.innerHTML = `
            <div class='delivery-content'>
              <div class='delivery-option'>
                <div class="delivery-title">
                  <span class='delivery-icon'>
                    <svg width="32" height="24" viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="9.25" y="9.75" width="13.5" height="13.5" fill="white"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M17.5 15H14.5V18.75H11.5V23.25H20.5V18.75H17.5V15Z" fill="#00D0BF"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M25.75 7.5L16 1.125L6.25 7.5V9L6.25 23.25H8.5L8.5 9H23.5V23.25H25.75V9V7.5Z" fill="#00D0BF"/>
                        <path d="M16.0026 0L5.17188 7.0957L5.17187 24H26.8331V7.0957L16.0026 0ZM22.351 22.5945H20.9764V18.1068H18.8488V13.739H13.1875L13.1875 18.1068H11.0598V22.5945H9.65393V9.90033L22.351 9.90033V22.5945ZM17.4431 18.1068H14.593L14.593 15.1445H17.4431V18.1068ZM15.2998 19.5125V22.5945H12.4653V19.5125H15.2998ZM16.7053 19.5125H19.5709V22.5945H16.7053V19.5125ZM25.4276 22.5945H23.7565V8.49463L8.24841 8.49463L8.24841 22.5945H6.57739L6.57739 7.85522L16.0026 1.68036L25.4276 7.85522V22.5945Z" fill="#064A45"/>
                      </svg>
                  </span>
                  <span class='delivery-title'>Pick Up</span>
                </div>
                <span class='delivery-price'>FREE</span>
              </div>
              <div class='delivery-stores'>
                <div class='delivery-store'>
                  <div class='store-header'>
                    <p class='store-name'>${itemStore?.location?.name}</p>
                    <a href='tel:${itemStore?.location?.address?.phone}' class='store-phone delivery-action'>${
          itemStore?.location?.address?.phone.replace(/\D*(\d{3})\D*(\d{4})\D*(\d{4})\D*/, '$1 $2 $3')
        }</a>
                  </div>
                  <div class='store-details'>
                    <p>
                      ${itemStore?.location?.address?.address1}<br>
                      ${itemStore?.location?.address?.city} ${itemStore?.location?.address?.province} ${
          itemStore?.location?.address?.zip
        }, ${itemStore?.location?.address?.country}<br>
                    </p>
                    <p class='store-actions'>
                    ${
                      store_link
                        ? `<a href='${store_link?.url}' target='_blank' class='delivery-action' style="display:none;"
                        >View Store Details</a
                      >`
                        : ''
                    }
                      <a
                        href='https://www.google.com/maps/dir/?api=1&destination=${
                          itemStore?.location?.address?.address1
                        }'
                        target='_blank'
                        class='delivery-action'
                        >Directions</a
                      >
                    </p>
                  </div>
                  ${renderOpeningHoursList(openingHours)}
                </div>
              </div>
            </div>`;

        pickUpStoreContainer.appendChild(pickUpElement);
      });
    }
  }
}

function parseOpeningHoursString(hoursStr) {
  if (!hoursStr || typeof hoursStr !== 'string') return null;

  const dayKeyMap = {
    Sun: 'sunday',
    Mon: 'monday',
    Tue: 'tuesday',
    Wed: 'wednesday',
    Thu: 'thursday',
    Fri: 'friday',
    Sat: 'saturday'
  };

  const result = {};

  hoursStr.split('|').forEach((segment) => {
    const trimmed = segment.trim();
    if (!trimmed) return;

    const [dayAbbrev, timeRange] = trimmed.split(',').map((s) => s.trim());
    const key = dayKeyMap[dayAbbrev];
    if (!key) return;

    if (timeRange) {
      result[key] = timeRange;
    }
  });

  return result;
}

function renderOpeningHoursList(openingHours) {
  if (!openingHours) return '';

  let hoursStr = openingHours;
  if (typeof openingHours === 'object' && openingHours !== null && typeof openingHours.value === 'string') {
    hoursStr = openingHours.value;
  }

  if (typeof hoursStr !== 'string' || !hoursStr.trim()) return '';

  const normalized = parseOpeningHoursString(hoursStr);
  if (!normalized || typeof normalized !== 'object') return '';

  const keysByIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const todayIndex = new Date().getDay();
  const todayKey = keysByIndex[todayIndex];
  const todayHours = normalized[todayKey];

  if (!todayHours) return '';

  return `<div class="delivery-store-details__hours-row"><span>Open Today:</span><span>${todayHours}</span></div>`;
}

// Fallback function for basic location input when Google Maps API is not available
function setupBasicLocationInput() {
  if (!input) return;

  const coordinatesResult = document.getElementById('coordinates-result');

  // Handle close popup functionality
  document.getElementById('close-popup').addEventListener('click', () => {
    const inputValue = input.value.trim();

    if (inputValue === '') {
      if (noResultsDiv) {
        noResultsDiv.style.display = 'block';
        return;
      }
    } else {
      if (noResultsDiv) {
        noResultsDiv.style.display = 'none';
      }

      // Save basic location data without coordinates
      const locationData = {
        address: inputValue,
        lat: null,
        lng: null,
        timestamp: Date.now()
      };

      localStorage.setItem('customerLocation', JSON.stringify(locationData));
      closeLocationPopup();
      checkCustomerLocation();
    }
  });

  // Load saved location
  const savedLocation = localStorage.getItem('customerLocation');
  if (savedLocation) {
    const locationData = JSON.parse(savedLocation);
    input.value = locationData.address;
    if (coordinatesResult) {
      coordinatesResult.innerHTML = locationData.lat && locationData.lng
        ? `Coordinates: Latitude ${locationData.lat}, Longitude ${locationData.lng}`
        : 'Coordinates not available (Google Maps API not loaded)';
    }
  }
}
